// AI Service — calls the Supabase Edge Function "ai-proxy"
// The AI API key NEVER touches the browser. Per-user-per-day rate limit enforced server-side.

import { supabase } from './supabase';

export async function callAI(systemPrompt, userMessage) {
  const { data: { session } } = await supabase.auth.getSession();

  if (!session) {
    throw new Error('You must be signed in to use AI features.');
  }

  const { data, error } = await supabase.functions.invoke('ai-proxy', {
    body: {
      system: systemPrompt,
      message: userMessage,
      max_tokens: 1024
    }
  });

  if (error) {
    let msg = error.message || 'AI request failed';
    try {
      const ctx = error.context;
      if (ctx && typeof ctx.json === 'function') {
        const errBody = await ctx.json();
        if (errBody?.error) msg = errBody.error;
      }
    } catch {}
    throw new Error(msg);
  }

  if (data?.error) {
    throw new Error(data.error);
  }

  return {
    text: data?.text || 'No response received.',
    usage: data?.usage || null
  };
}

// Fetch current usage without consuming a credit
export async function getAIUsage(dailyLimit = 20) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return null;

  const { data, error } = await supabase.rpc('get_ai_usage_today', { daily_limit: dailyLimit });
  if (error) return null;
  return data;
}

// ── PROMPT TEMPLATES ──

export const SYSTEM_TRADE_REVIEW = `You are an experienced XAUUSD (gold) trading coach reviewing a student's trade. Your job is to give honest, specific, actionable feedback — not generic praise.

The student is using a 5-condition framework:
1. 4H structural bias confirmed
2. Liquidity level identified
3. Liquidity sweep confirmed on candle close
4. 1H Change of Character (CHoCH)
5. 15M entry trigger

Key rules they follow:
- 1% max risk per trade
- Minimum 1:2 RR
- Trade only London and NY sessions
- No trades 30 min before/after major news
- Complete 12-point pre-trade checklist

When reviewing, focus on:
- Whether the setup matched their framework
- If the R-multiple result aligns with what the framework predicts
- Specific lessons from their notes and emotional state
- One concrete thing to do differently next time
- One thing they did well (only if genuine)

Be direct and concise. Use 4-6 short paragraphs maximum. No bullet points. No emojis. Address the trader as "you" directly. Sound like a calm, experienced mentor — not a cheerleader.`;

export const SYSTEM_DAILY_REVIEW = `You are an experienced trading coach reviewing a trader's recent performance. The trader follows a strict XAUUSD framework with 1% risk per trade, minimum 1:2 RR, and a 12-point pre-trade checklist.

Analyze the trades provided and identify:
- The single most important pattern in their performance (good or bad)
- Whether their checklist discipline is correlating with results
- If certain sessions, setups, or emotional states are hurting them
- One specific action item for the next trading session

Be honest. If the trader is undisciplined, say so. If they're trading well, acknowledge it specifically. Use prose only — 5-7 short paragraphs. No bullet points, no emojis. Sound like a respected mentor reviewing their student's journal.`;

export const SYSTEM_SETUP_VALIDATOR = `You are an XAUUSD trading coach evaluating a potential trade setup BEFORE the trader enters. Your job is to stress-test their thinking using the 5-condition framework:

1. 4H structural bias confirmed
2. Liquidity level identified
3. Liquidity sweep confirmed on candle close
4. 1H Change of Character (CHoCH)
5. 15M entry trigger

For the setup the trader describes, do this:
- Rate it on a scale of 1-10 (with 7+ being a valid A-setup)
- Identify which conditions are clearly met
- Identify which conditions are missing or weak
- Calculate expected RR based on their levels
- Give a clear recommendation: TAKE / SKIP / WAIT FOR MORE CONFIRMATION

Be honest and direct. Most setups should NOT be taken — that's the framework working. Use 4-6 short paragraphs. No bullet points, no emojis. Sound like a calm, skeptical mentor.`;

export function formatTradeForAI(trade) {
  return `Here is the trade to review:

Date: ${trade.trade_date}
Direction: ${trade.direction}
Session: ${trade.session || 'Unspecified'}
Entry: $${trade.entry_price}
Stop Loss: $${trade.stop_loss}
Exit: $${trade.exit_price}
R-Multiple: ${trade.r_multiple !== null ? trade.r_multiple + 'R' : 'N/A'}
Outcome: ${trade.outcome}
Setup Type: ${trade.setup_type || 'Not specified'}
Tags: ${trade.tags || 'None'}
Checklist Complete: ${trade.checklist_complete}
Emotional State: ${trade.emotional_state || 'Not recorded'}
Trader's Notes: ${trade.notes || 'No notes provided'}

Review this trade as a coach would. Be specific to what's shown above.`;
}

export function formatDailyTradesForAI(trades) {
  const summary = trades.map((t, i) => (
    `${i+1}. ${t.trade_date} · ${t.direction} ${t.session || ''} · ${t.outcome} · ${t.r_multiple !== null ? t.r_multiple + 'R' : '—'} · Checklist: ${t.checklist_complete} · Setup: ${t.setup_type || 'untagged'} · State: ${t.emotional_state || 'unrecorded'}${t.notes ? '\n   Notes: ' + t.notes : ''}`
  )).join('\n\n');

  const wins = trades.filter(t => t.outcome === 'Win').length;
  const losses = trades.filter(t => t.outcome === 'Loss').length;
  const totalR = trades.reduce((s, t) => s + (parseFloat(t.r_multiple) || 0), 0);
  const fullChecklist = trades.filter(t => t.checklist_complete === 'Yes').length;

  return `Review these ${trades.length} recent trades:

Quick stats:
- Wins: ${wins} · Losses: ${losses}
- Total R: ${totalR > 0 ? '+' : ''}${totalR.toFixed(2)}R
- Full checklist completed: ${fullChecklist}/${trades.length}

Trade-by-trade:

${summary}

Give a coach's review of this performance. Identify the most important pattern and give one specific action item.`;
}
